import { createRouter, createWebHistory } from "vue-router";
import routes from "@/router/routes";
import { useUserStore } from "@/store/user";
import { computed, ref } from "vue";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { useCoachStore } from "@/store/coach";
import { useSupportChat } from "@/composables/useSupportChat";
import { useManageSubscription } from "@/composables/useManageSubscription";
import { useMigrateBanner } from "@/composables/useMigrateBanner";
import { useFreeTrialContext } from "@/composables/useFreeTrialContext";
dayjs.extend(isSameOrBefore);

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  },
});

router.beforeEach((to, from, next) => {
  const userStore = useUserStore();
  const coachStore = useCoachStore();
  const { isCoach, isAnyAuthorized } = useSupportChat();
  const freePageArr: string[] = [
    "Forgot",
    "Reset",
    "changeEmail",
    "PrivacyPolicy",
    "TermsOfUse",
    "OpenMobileApp",
    "UpgradeOptions",
  ];
  const { isActiveMainSubs, isActiveManualSubs, isPastDueMainSubs } =
    useManageSubscription();
  const { isMigratedToApp } = useMigrateBanner();
  const { clear } = useFreeTrialContext();
  const isLoginRoute = to.meta?.isLoginPage;
  const freePage = to.meta?.freePage;
  const skipSubscriptionCheck = to.meta?.skipSubscriptionCheck || false;

  if (
    ["Login", "Registration"].includes(to.name as string) ||
    (to.name === "RegistrationTrial" && !isAnyAuthorized.value)
  ) {
    clear();
  }

  if (
    ["Login", "Registration", "RegistrationTrial"].includes(
      to.name as string
    ) &&
    Object.prototype.hasOwnProperty.call(to.query, "freeTrial")
  ) {
    const query = { ...to.query };
    delete query.freeTrial;
    return next({
      name: to.name as string,
      params: to.params,
      query,
      hash: to.hash,
      replace: true,
    });
  }

  // need in the future
  // const isRefreshToken = computed(() =>
  //   isCoach.value ? coachStore.coachAccessToken : userStore.accessToken
  // );
  // const isAuthorized = computed(() => {
  //   return !!isRefreshToken.value;
  // });

  if (isAnyAuthorized.value) {
    if (isCoach.value) {
      if (!freePage && !to.meta?.coachPage) {
        return next({ name: "CoachChat" });
      }
    } else {
      const noActiveSubscription = computed(() => {
        return (
          userStore.data?.subscription == null ||
          (!isActiveMainSubs.value && !isActiveManualSubs.value)
        );
      });
      if (
        isPastDueMainSubs.value &&
        (!userStore.settings.subscription.failedStatus ||
          dayjs().diff(userStore.settings.subscription.failedStatus, "day") >=
            1)
      ) {
        userStore.settings.subscription.failedStatus =
          dayjs().format("YYYY-MM-DD");
      }

      if (noActiveSubscription.value && !freePage && !skipSubscriptionCheck) {
        return next({ name: "StubPage" });
      } else if (
        !userStore.data?.isOnboard &&
        !freePage &&
        !isMigratedToApp.value
      ) {
        return next({
          name: "Onboarding",
        });
      }

      if (
        userStore.data?.isOnboard &&
        (to.name === "Onboarding" || to.name === "OnboardingStep")
      ) {
        return next({ name: "Dashboard" });
      }

      if (
        (isActiveMainSubs.value || isActiveManualSubs.value) &&
        to.name === "StubPage"
      ) {
        return next({ name: "Dashboard" });
      }

      if (
        (isLoginRoute && !freePageArr.includes(to.name as string)) ||
        to.meta?.coachPage
      ) {
        return next({ name: "Dashboard" });
      }
    }
  } else {
    if (!isLoginRoute) {
      return next({ name: "Login", query: { targetLink: to.fullPath } });
    }
  }
  return next();
});

router.onError((error) => {
  if (
    /ChunkLoadError:.*failed./i.test(error.message) ||
    /Loading.*chunk.*failed./i.test(error.message)
  ) {
    window.location.reload();
  }
});

export default router;
